const PACKAGES = [
  {
    name: "Starter Website",
    tagline: "Get Online, Get Found",
    price: "$247",
    period: "one-time",
    popular: false,
    features: [
      "Professional website (1–3 sections)",
      "Mobile-optimized design",
      "Direct WhatsApp button",
      "Services section",
      "Contact form",
      "Instagram integration",
    ],
    bonuses: [
      "Basic sales copywriting",
      "Delivered in 3–5 days",
    ],
    cta: "Get Started",
    icon: "ri-layout-line",
  },
  {
    name: "Growth Website",
    tagline: "Convert Visitors to Clients",
    price: "$497",
    period: "one-time",
    popular: true,
    features: [
      "Everything in Starter, plus:",
      "Up to 5 sections",
      "More personalized design",
      "Optimized copywriting (texts that convert)",
      "Sales-focused structure",
      "Basic Google optimization (SEO)",
    ],
    bonuses: [
      "Post-delivery adjustments",
      "Quick strategy consultation",
    ],
    cta: "Most Popular",
    icon: "ri-rocket-line",
  },
  {
    name: "Premium Revenue System",
    tagline: "Full Business Growth Engine",
    price: "$797–$997",
    period: "one-time",
    popular: false,
    features: [
      "Everything in Growth, plus:",
      "Up to 7 sections",
      "Fully custom design",
      "Booking or payment integration",
      "Basic conversion strategy",
      "Complete setup — ready to sell",
    ],
    bonuses: [
      "Initial support included",
      "Post-launch adjustments",
    ],
    cta: "Go Premium",
    icon: "ri-vip-crown-line",
  },
];

export default function ServicesSection() {
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="services" className="bg-[#0A1628] py-24 md:py-32 relative overflow-hidden">
      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      {/* Gold glow top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-32 opacity-20 blur-3xl bg-[#D4AF37]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 mb-5">
            <div className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Revenue Systems
            </span>
            <div className="h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2
            className="text-white font-black text-4xl md:text-6xl leading-tight mb-5"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Choose Your
            <br />
            <span
              style={{
                background: "linear-gradient(135deg, #D4AF37 0%, #f5d980 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Growth Path
            </span>
          </h2>
          <p className="text-white/50 text-lg" style={{ fontFamily: "'Inter', sans-serif" }}>
            Every package is built to generate real business results.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {PACKAGES.map((pkg, i) => (
            <div
              key={pkg.name}
              className={`relative rounded-2xl p-8 flex flex-col h-full transition-all duration-300 hover:-translate-y-2 cursor-default
                ${pkg.popular
                  ? "bg-[#D4AF37]/10 border-2 border-[#D4AF37] md:-translate-y-4"
                  : "bg-white/5 border border-[#D4AF37]/20 hover:border-[#D4AF37]/50"
                }`}
            >
              {/* Popular Badge */}
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#070d1a] text-xs font-black tracking-widest uppercase px-5 py-1.5 rounded-full whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Most Popular
                </div>
              )}

              {/* Icon */}
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-6 ${pkg.popular ? "bg-[#D4AF37]/20" : "bg-[#D4AF37]/10"}`}>
                <i className={`${pkg.icon} text-[#D4AF37] text-2xl`} />
              </div>

              {/* Package name */}
              <p className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {pkg.name}
              </p>
              <p className="text-white/60 text-sm mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                {pkg.tagline}
              </p>

              {/* Price */}
              <div className="mb-8">
                <span className="text-white/50 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>Starting at</span>
                <div className="flex items-end gap-1 flex-wrap">
                  {pkg.price.includes("–") ? (
                    <span className="text-white font-black text-4xl leading-none" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {pkg.price}
                    </span>
                  ) : (
                    <>
                      <span className="text-[#D4AF37] text-2xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>$</span>
                      <span className="text-white font-black text-5xl leading-none" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                        {pkg.price.replace("$", "")}
                      </span>
                    </>
                  )}
                </div>
                <span className="text-white/40 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>{pkg.period}</span>
              </div>

              {/* Features */}
              <ul className="flex flex-col gap-3 mb-6 flex-1">
                {pkg.features.map((feat) => (
                  <li key={feat} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-check-line text-[#D4AF37] text-base" />
                    </div>
                    <span className="text-white/70 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {feat}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Bonuses */}
              {pkg.bonuses && pkg.bonuses.length > 0 && (
                <div className="mb-8 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4">
                  <p className="text-[#D4AF37] text-xs font-black tracking-widest uppercase mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Bonus Included
                  </p>
                  <ul className="flex flex-col gap-2">
                    {pkg.bonuses.map((bonus) => (
                      <li key={bonus} className="flex items-start gap-2">
                        <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                          <i className="ri-gift-line text-[#D4AF37] text-sm" />
                        </div>
                        <span className="text-white/80 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {bonus}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(!pkg.bonuses || pkg.bonuses.length === 0) && <div className="mb-4" />}

              {/* CTA */}
              <button
                onClick={() => scrollTo("cta")}
                className={`w-full py-4 rounded-full font-bold text-sm tracking-wider uppercase transition-all duration-300 cursor-pointer whitespace-nowrap
                  ${pkg.popular
                    ? "bg-[#D4AF37] text-[#070d1a] hover:bg-[#c4a030]"
                    : "bg-transparent border-2 border-[#D4AF37]/40 text-white hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
                  }`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {pkg.popular ? "Select Plan" : "Get Started"}
              </button>
            </div>
          ))}
        </div>

        {/* Note */}
        <p className="text-center text-white/30 text-sm mt-10" style={{ fontFamily: "'Inter', sans-serif" }}>
          All packages include hosting setup, launch support, and revision rounds. Custom quotes available.
        </p>
      </div>
    </section>
  );
}
